import type { User } from '../../types';
import type {
  ApprovalOrderMode,
  ChangeFormField,
  CreatorApproverRole,
  PlanEditorRole,
} from '../../types/changes';
import { isChangeManager, isChangeRequesterOnlyForOps } from './changePermissions';

export const TRACK_RISCOS = 'riscos_impactos';
export const TRACK_CAMUD = 'camud_go_no_go';
export const TRACK_GESTAO = 'gestao_mudancas';

export const APPROVAL_TRACKS = [
  { track: TRACK_RISCOS, label: 'Riscos e impactos' },
  { track: TRACK_CAMUD, label: 'CAMUD Go/No-Go' },
  { track: TRACK_GESTAO, label: 'Gestor de mudanças' },
] as const;

export type ApprovalTrack = (typeof APPROVAL_TRACKS)[number]['track'];

export interface ApprovalPlanArea {
  group_id: number;
  name?: string | null;
}

export interface ApprovalPlanPerson {
  group_id: number;
  group_name?: string | null;
  assignee_id: number;
  assignee_name?: string | null;
}

export interface ApprovalPlanTrack {
  track: string;
  label: string;
  areas: ApprovalPlanArea[];
  people?: ApprovalPlanPerson[];
}

export interface ApprovalPlan {
  tracks: ApprovalPlanTrack[];
}

export interface ApprovalPlanDraft {
  enabled: Record<ApprovalTrack, boolean>;
  groupIds: Record<ApprovalTrack, number[]>;
  people: Record<ApprovalTrack, ApprovalPlanPerson[]>;
}

export interface ApprovalSettingsTrack {
  track: string;
  label: string;
  enabled: boolean;
}

export interface ApprovalSettings {
  enable_riscos_impactos: boolean;
  enable_camud_go_no_go: boolean;
  enable_gestao_mudancas?: boolean;
  creator_approver_roles?: CreatorApproverRole[];
  plan_editor_roles?: PlanEditorRole[];
  approval_order_mode?: ApprovalOrderMode;
  form_editable_statuses?: string[];
  tracks: ApprovalSettingsTrack[];
  form_fields?: ChangeFormField[];
}

export const PLAN_EDITOR_ROLE_OPTIONS: Array<{ value: PlanEditorRole; label: string }> = [
  { value: 'admin', label: 'Administrador' },
  { value: 'gestor', label: 'Gestor de mudanças' },
  { value: 'criador', label: 'Criador da mudança' },
  { value: 'dono', label: 'Dono da mudança' },
  { value: 'solicitante', label: 'Solicitante da mudança' },
];

export const DEFAULT_PLAN_EDITOR_ROLES: PlanEditorRole[] = ['admin', 'gestor', 'criador'];

export function normalizePlanEditorRoles(raw?: string[] | null): PlanEditorRole[] {
  const allowed = new Set(PLAN_EDITOR_ROLE_OPTIONS.map((item) => item.value));
  const cleaned = (raw || []).filter((item): item is PlanEditorRole =>
    allowed.has(item as PlanEditorRole),
  );
  return cleaned.length ? cleaned : [...DEFAULT_PLAN_EDITOR_ROLES];
}

export const DEFAULT_APPROVAL_SETTINGS: ApprovalSettings = {
  enable_riscos_impactos: false,
  enable_camud_go_no_go: true,
  enable_gestao_mudancas: true,
  creator_approver_roles: ['criador', 'gestor'],
  plan_editor_roles: [...DEFAULT_PLAN_EDITOR_ROLES],
  approval_order_mode: 'livre',
  form_editable_statuses: ['avaliar', 'agendado'],
  tracks: [
    { track: TRACK_RISCOS, label: 'Riscos e impactos', enabled: false },
    { track: TRACK_CAMUD, label: 'CAMUD Go/No-Go', enabled: true },
    { track: TRACK_GESTAO, label: 'Gestor de mudanças', enabled: true },
  ],
  form_fields: [],
};

export const emptyApprovalPlanDraft = (): ApprovalPlanDraft => ({
  enabled: { [TRACK_RISCOS]: false, [TRACK_CAMUD]: false, [TRACK_GESTAO]: false },
  groupIds: { [TRACK_RISCOS]: [], [TRACK_CAMUD]: [], [TRACK_GESTAO]: [] },
  people: { [TRACK_RISCOS]: [], [TRACK_CAMUD]: [], [TRACK_GESTAO]: [] },
});

export function isApprovalTrackEnabled(
  settings: ApprovalSettings | null | undefined,
  track: string,
) {
  if (!settings) return true;
  const found = (settings.tracks || []).find((item) => item.track === track);
  if (found) return Boolean(found.enabled);
  if (track === TRACK_RISCOS) return Boolean(settings.enable_riscos_impactos);
  if (track === TRACK_CAMUD) return Boolean(settings.enable_camud_go_no_go);
  if (track === TRACK_GESTAO) return settings.enable_gestao_mudancas !== false;
  return false;
}

export function visibleApprovalTracks(
  settings?: ApprovalSettings | null,
  draft?: ApprovalPlanDraft | null,
) {
  return APPROVAL_TRACKS.filter((item) => {
    if (isApprovalTrackEnabled(settings, item.track)) return true;
    if (draft?.enabled[item.track]) return true;
    return (draft?.groupIds[item.track] || []).length > 0 || (draft?.people[item.track] || []).length > 0;
  });
}

export function labelApprovalTrack(track?: string | null) {
  if (track === 'emergencial') return 'Emergencial';
  if (track === 'transporte') return 'Transporte';
  if (track === 'criador_gestor') return 'Criador / gestor';
  const found = APPROVAL_TRACKS.find((item) => item.track === track);
  return found?.label || 'Aprovação';
}

export type ApprovalSlotSummary = {
  id?: number;
  group_id?: number | null;
  group_name?: string | null;
  track?: string | null;
  status?: string | null;
  assignee_name?: string | null;
  assignment_kind?: string | null;
  assignee_id?: number | null;
};

export function approvalSlotStatusLabel(status?: string | null): string {
  if (status === 'aprovado') return 'aprovado';
  if (status === 'rejeitado' || status === 'reprovado') return 'reprovado';
  if (status === 'pendente') return 'pendente';
  return status || '—';
}

export function approvalSlotDisplayLabel(slot: ApprovalSlotSummary): string {
  if (slot.track === TRACK_GESTAO && !slot.group_id) {
    return 'Gestor de mudanças';
  }
  const trackLabel = labelApprovalTrack(slot.track);
  if (slot.assignment_kind === 'pessoa') {
    const person = slot.assignee_name || (slot.assignee_id ? `Usuário #${slot.assignee_id}` : 'Pessoa');
    const area = slot.group_name || 'área';
    return `${person} · ${area} (${trackLabel})`;
  }
  const area = slot.group_name || 'Área';
  return `${area} (${trackLabel})`;
}

function gestaoManagerSlot(
  approvals?: ApprovalSlotSummary[] | null,
): ApprovalSlotSummary | undefined {
  return (approvals || []).find((item) => item.track === TRACK_GESTAO && !item.group_id);
}

/** Texto da confirmação do reset atômico para Agendado. */
export function buildResetToScheduledConfirmDetails(): string {
  return [
    '• Aprovações de comitê (CAMUD, Riscos e impactos, gestor de mudanças)',
    '• Aprovações de Transporte (GTEC, GSERV-I, gerente imediato e slots extras)',
    '• Subtarefas internas (canceladas; histórico mantido)',
    '• Subtarefas externas / chamados no Incidente (registro cancelado aqui; o chamado NÃO é fechado)',
    '• Status da mudança volta para Agendado',
  ].join('\n');
}

/** Texto da modal ao cancelar todas as aprovações de área em Autorizar. */
export function buildVoidAllApprovalsConfirmDetails(
  approvals?: ApprovalSlotSummary[] | null,
): string {
  const active = activeCommitteeApprovalSlots(approvals);
  const groupSlots = active.filter((item) => item.group_id);
  const lines: string[] = groupSlots.map(
    (slot) => `• ${approvalSlotDisplayLabel(slot)} — ${approvalSlotStatusLabel(slot.status)}`,
  );
  const gestor = gestaoManagerSlot(active);
  if (gestor) {
    if (gestor.status && gestor.status !== 'pendente') {
      lines.push(
        `• Gestor de mudanças — decisão ${approvalSlotStatusLabel(gestor.status)} será anulada`,
      );
    }
    lines.push(
      '• Card do gestor de mudanças some desta aba até você salvar o plano de novo (toggle Gestor de mudanças).',
    );
  }
  if (!lines.length) {
    lines.push('• Nenhuma aprovação de área ativa.');
  }
  lines.push('• Comitê no bloco “Quem aprova esta mudança” será esvaziado; salve o plano de novo.');
  return lines.join('\n');
}

/** Texto da modal ao salvar o plano de aprovação (rascunho atual). */
export function buildSaveApprovalPlanConfirmDetails(
  draft: ApprovalPlanDraft,
  areaNamesById: Record<number, string> = {},
): string {
  const payload = buildApprovalPlanPayload(draft);
  const lines: string[] = payload.tracks.map((item) => {
    const trackLabel = labelApprovalTrack(item.track);
    if (item.track === TRACK_GESTAO) {
      return `• ${trackLabel}: admin ou usuário com flag Gestor de mudanças`;
    }
    const areaLabels = item.group_ids.map((id) => areaNamesById[id] || `Área #${id}`);
    const trackKey = item.track as ApprovalTrack;
    const peopleLabels = (draft.people[trackKey] || []).map(
      (person) =>
        `${person.assignee_name || `Usuário #${person.assignee_id}`} · ${
          person.group_name || areaNamesById[person.group_id] || `Área #${person.group_id}`
        }`,
    );
    const parts = [...areaLabels, ...peopleLabels];
    return `• ${trackLabel}: ${parts.length ? parts.join(', ') : '—'}`;
  });
  if (!lines.length) {
    lines.push('• Nenhum grupo ou gestor selecionado.');
  }
  lines.push('• A aba Aprovações será sincronizada com este comitê.');
  return lines.join('\n');
}

/** Texto da modal ao cancelar uma aprovação de área. */
export function buildVoidSlotConfirmDetails(slot: ApprovalSlotSummary): string {
  return [
    `• Remove: ${approvalSlotDisplayLabel(slot)} — ${approvalSlotStatusLabel(slot.status)}`,
    '• Se o gestor já tiver decidido, a decisão dele volta para Pendente (o card continua; ele precisa decidir de novo).',
    '• As demais áreas do comitê continuam valendo.',
  ].join('\n');
}

export function draftFromPlan(
  plan?: ApprovalPlan | null,
  settings?: ApprovalSettings | null,
): ApprovalPlanDraft {
  const draft = emptyApprovalPlanDraft();
  let hasContent = false;
  let hasGestaoMarker = false;
  for (const item of plan?.tracks || []) {
    if (item.track === TRACK_RISCOS || item.track === TRACK_CAMUD || item.track === TRACK_GESTAO) {
      const ids = (item.areas || []).map((area) => area.group_id);
      const people = item.people || [];
      if (item.track === TRACK_GESTAO) {
        // Gestor: só interruptor (sem áreas). Presença no plano = ligado.
        hasGestaoMarker = true;
        draft.enabled[TRACK_GESTAO] = true;
        draft.groupIds[TRACK_GESTAO] = [];
        draft.people[TRACK_GESTAO] = [];
        hasContent = true;
        continue;
      }
      draft.enabled[item.track] = ids.length > 0 || people.length > 0;
      draft.groupIds[item.track] = ids;
      draft.people[item.track] = people;
      if (ids.length || people.length) hasContent = true;
    }
  }
  if (!hasContent) {
    if (isApprovalTrackEnabled(settings ?? DEFAULT_APPROVAL_SETTINGS, TRACK_CAMUD)) {
      draft.enabled[TRACK_CAMUD] = true;
    }
    if (isApprovalTrackEnabled(settings ?? DEFAULT_APPROVAL_SETTINGS, TRACK_GESTAO)) {
      draft.enabled[TRACK_GESTAO] = true;
    }
  } else if (
    !hasGestaoMarker &&
    isApprovalTrackEnabled(settings ?? DEFAULT_APPROVAL_SETTINGS, TRACK_GESTAO)
  ) {
    // Plano já salvo sem gestor: deixa desligado (opt-out por mudança).
    draft.enabled[TRACK_GESTAO] = false;
  }
  return draft;
}

export function buildApprovalPlanPayload(draft: ApprovalPlanDraft) {
  return {
    tracks: APPROVAL_TRACKS.filter((item) => {
      if (!draft.enabled[item.track]) return false;
      if (item.track === TRACK_GESTAO) return true;
      return draft.groupIds[item.track].length > 0 || (draft.people[item.track] || []).length > 0;
    }).map((item) => ({
      track: item.track,
      group_ids: item.track === TRACK_GESTAO ? [] : draft.groupIds[item.track],
      people:
        item.track === TRACK_GESTAO
          ? []
          : (draft.people[item.track] || []).map((person) => ({
              group_id: person.group_id,
              assignee_id: person.assignee_id,
            })),
    })),
  };
}

export function validateApprovalPlan(draft: ApprovalPlanDraft, { required = true } = {}) {
  const enabled = APPROVAL_TRACKS.filter((item) => draft.enabled[item.track]);
  if (required && !enabled.length) {
    return 'Escolha ao menos um grupo habilitado (áreas) ou ligue o Gestor de mudanças.';
  }
  const missingAreas = enabled.find((item) => {
    if (item.track === TRACK_GESTAO) return false;
    return draft.groupIds[item.track].length === 0 && (draft.people[item.track] || []).length === 0;
  });
  if (missingAreas) {
    return `Associe ao menos uma área ou pessoa em ${missingAreas.label}.`;
  }
  return null;
}

export function countApprovalPlanAreas(plan?: ApprovalPlan | null) {
  return (plan?.tracks || []).reduce(
    (total, track) => total + (track.areas?.length || 0) + (track.people?.length || 0),
    0,
  );
}

export function canEditApprovalPlan(
  change: {
    created_by?: number | null;
    requester_id?: number | null;
    change_owner_id?: number | null;
  },
  user?: Pick<User, 'id' | 'role' | 'can_manage_changes'> | null,
  settings?: Pick<ApprovalSettings, 'plan_editor_roles'> | null,
) {
  if (!user?.id) return false;
  const roles = normalizePlanEditorRoles(settings?.plan_editor_roles);
  if (roles.includes('admin') && user.role === 'admin') return true;
  if (roles.includes('gestor') && Boolean(user.can_manage_changes)) return true;
  if (roles.includes('criador') && change.created_by === user.id) return true;
  if (roles.includes('dono') && change.change_owner_id === user.id) return true;
  if (roles.includes('solicitante') && change.requester_id === user.id) return true;
  return false;
}

/** Quem monta o plano (cadastro plan_editor_roles): sem flag nem vê o bloco na ficha. */
export function canViewApprovalPlan(
  change: Parameters<typeof canEditApprovalPlan>[0],
  user?: Parameters<typeof canEditApprovalPlan>[1],
  settings?: Parameters<typeof canEditApprovalPlan>[2],
) {
  return canEditApprovalPlan(change, user, settings);
}

/** Cancelar / redisparar Transporte: gestor, admin, dono ou solicitante (não criador nem aprovador). */
export function canManageTransportApprovalOps(
  change: {
    requester_id?: number | null;
    change_owner_id?: number | null;
  },
  user?: Pick<User, 'id' | 'role' | 'can_manage_changes'> | null,
) {
  if (!user?.id) return false;
  if (isChangeManager(user as User)) return true;
  if (change.change_owner_id === user.id) return true;
  if (change.requester_id === user.id) return true;
  return false;
}

/** Cancelar / nova rodada de aprovações — solicitante puro não redispara. */
export function canManageApprovalRespawn(
  change: {
    created_by?: number | null;
    requester_id?: number | null;
    change_owner_id?: number | null;
  },
  user?: Pick<User, 'id' | 'role' | 'can_manage_changes'> | null,
  settings?: Pick<ApprovalSettings, 'plan_editor_roles'> | null,
) {
  if (isChangeRequesterOnlyForOps(change, user)) return false;
  if (isChangeManager(user as User)) return true;
  return canEditApprovalPlan(change, user, settings);
}

function isCommitteeApprovalSlot(item: {
  group_id?: number | null;
  track?: string | null;
}) {
  const track = (item.track || '').trim().toLowerCase();
  // Transporte/emergencial ficam na aba Transporte, não no badge de comitê.
  if (track === 'transporte' || track === 'emergencial') {
    return false;
  }
  return Boolean(item.group_id) || track === 'criador_gestor' || track === TRACK_GESTAO;
}

/** Espelha active_approval_slots do backend: última rodada por chave, sem canceladas. */
export function activeCommitteeApprovalSlots<
  T extends {
    id?: number;
    group_id?: number | null;
    status?: string | null;
    track?: string | null;
    assignee_id?: number | null;
  },
>(approvals?: T[] | null): T[] {
  const committee = (approvals || []).filter(isCommitteeApprovalSlot);
  return latestApprovalSlotsByKey(committee).filter((item) => item.status !== 'cancelado');
}

export function approvalSlotsStillPending(
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null,
) {
  const slots = activeCommitteeApprovalSlots(approvals);
  if (slots.length === 0) return true;
  if (slots.some((item) => item.status === 'aprovado')) return false;
  if (slots.every((item) => item.status === 'pendente')) return true;
  if (slots.every((item) => item.status === 'rejeitado' || item.status === 'reprovado')) return true;
  return false;
}

export function hasCanceledCommitteeApproval(
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null,
) {
  return (approvals || []).some(
    (item) =>
      item.status === 'cancelado' &&
      Boolean(item.group_id || item.track === 'criador_gestor' || item.track === 'gestao_mudancas'),
  );
}

export function hasDecidedCommitteeApproval(
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null,
) {
  const slots = (approvals || []).filter(
    (item) => item.group_id || item.track === 'criador_gestor' || item.track === 'gestao_mudancas',
  );
  return slots.some(
    (item) => item.status && item.status !== 'pendente' && item.status !== 'cancelado',
  );
}

/** Bloqueia remontar plano em Agendado se o comitê já aprovou algum slot. */
export function committeeHasApproved(
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null,
) {
  const slots = (approvals || []).filter(
    (item) => item.group_id || item.track === 'criador_gestor' || item.track === 'gestao_mudancas',
  );
  return slots.some((item) => item.status === 'aprovado');
}

/** Mesma chave do backend (track, grupo, pessoa): só a rodada vigente por área. */
export function approvalSlotAreaKey(slot: {
  track?: string | null;
  group_id?: number | null;
  assignee_id?: number | null;
}) {
  return `${slot.track ?? ''}|${slot.group_id ?? ''}|${slot.assignee_id ?? ''}`;
}

export function isRejectedTransportApprovalStatus(status?: string | null) {
  return status === 'rejeitado' || status === 'reprovado';
}

/** Espelha o texto gravado pelo backend ao redisparar Transporte. */
export const TRANSPORT_APPROVAL_SUPERSEDED_NOTE =
  'Decisão substituída por nova rodada de Transporte.';

export const TRANSPORT_REJECTION_INVALIDATED_NOTE =
  'Decisão invalidada por reprovação em Transporte';

export function transportSlotSupersededByRespawn(justification?: string | null) {
  if (!justification?.includes(TRANSPORT_APPROVAL_SUPERSEDED_NOTE)) return false;
  return true;
}

export function transportSlotInvalidatedByRejection(justification?: string | null) {
  return Boolean(
    justification?.includes(TRANSPORT_REJECTION_INVALIDATED_NOTE) ||
      justification?.includes('Pendência anulada por reprovação em Transporte'),
  );
}

/** Pendente (bloco) → aprovado → reprovado → substituída por redisparo. */
export function transportApprovalDisplaySortRank(
  status?: string | null,
  justification?: string | null,
) {
  if (status === 'pendente') return 0;
  if (transportSlotSupersededByRespawn(justification)) return 3;
  if (status === 'aprovado') return 1;
  if (isRejectedTransportApprovalStatus(status)) return 2;
  if (status === 'cancelado') return 9;
  return 4;
}

/** Áreas fixas / fluxo configurado que recebem aprovação de Transporte nesta mudança. */
export function listTransportApprovalTargetLabels(
  change?: {
    emergency_approval?: { steps?: Array<{ group_id?: number; group_name?: string | null }> } | null;
    approvals?: Array<{ track?: string | null; group_id?: number | null; group_name?: string | null }> | null;
  } | null,
): string[] {
  const fromSteps = (change?.emergency_approval?.steps || [])
    .map((step) => step.group_name || (step.group_id != null ? `Área #${step.group_id}` : ''))
    .filter(Boolean);
  if (fromSteps.length) return fromSteps;
  const seen = new Set<string>();
  const fromSlots: string[] = [];
  for (const slot of change?.approvals || []) {
    if (slot.track !== 'transporte' || !slot.group_id) continue;
    const label = slot.group_name || `Área #${slot.group_id}`;
    if (seen.has(label)) continue;
    seen.add(label);
    fromSlots.push(label);
  }
  return fromSlots;
}

/** Ordem e áreas que o backend usa ao redisparar (cadastro + gerente imediato no momento do clique). */
export function formatTransportDispatchStepLines(
  change?: {
    emergency_approval?: {
      steps?: Array<{
        group_id?: number;
        group_name?: string | null;
        kind?: string | null;
        sort_order?: number | null;
      }>;
      requester_area_first?: boolean | null;
      unlock_others_after_first?: boolean | null;
    } | null;
  } | null,
): string[] {
  const steps = change?.emergency_approval?.steps || [];
  if (!steps.length) {
    return listTransportApprovalTargetLabels(change).map((name, index) => `${index + 1}. ${name}`);
  }
  const parallel = change?.emergency_approval?.unlock_others_after_first !== false;
  return steps.map((step, index) => {
    const order = step.sort_order ?? index + 1;
    const name = step.group_name || (step.group_id != null ? `Área #${step.group_id}` : 'Área');
    if (step.kind === 'requester_area') {
      return `${order}. ${name} — 1ª aprovação (gerente imediato / grupo do solicitante)`;
    }
    if (order === 2 && parallel && steps.length > 2) {
      return `${order}. ${name} — após o 1º passo, em paralelo com as demais fixas`;
    }
    return `${order}. ${name}`;
  });
}

export function buildTransportForceRespawnConfirmDetails(labels: string[]) {
  if (!labels.length) {
    return 'Nenhuma área de Transporte configurada para esta mudança.';
  }
  return [
    'O sistema lê o cadastro atual (fluxo emergencial, Grupos e gerente imediato) e abre pendências novas.',
    'Serão canceladas as pendências atuais desta rodada e redisparadas as áreas:',
    ...labels.map((name) => `• ${name}`),
    'Decisões anteriores permanecem no histórico (canceladas ou substituídas). O status da mudança (ex.: Agendado) não muda.',
  ].join('\n');
}

/** Comitê (CAMUD/Riscos): pendentes e decisões vigentes primeiro; canceladas por último. */
export function committeeApprovalDisplaySortRank(status?: string | null) {
  if (status === 'pendente') return 0;
  if (status === 'aprovado') return 1;
  if (status === 'rejeitado' || status === 'reprovado') return 2;
  if (status === 'cancelado') return 9;
  return 5;
}

export function sortCommitteeApprovalSlotsForDisplay<
  T extends {
    id?: number;
    group_id?: number | null;
    status?: string | null;
    order_index?: number | null;
  },
>(slots: T[]): T[] {
  return [...slots].sort((a, b) => {
    const byStatus =
      committeeApprovalDisplaySortRank(a.status) - committeeApprovalDisplaySortRank(b.status);
    if (byStatus !== 0) return byStatus;
    const byOrder = (a.order_index ?? 0) - (b.order_index ?? 0);
    if (byOrder !== 0) return byOrder;
    const byGroup = (a.group_id ?? 0) - (b.group_id ?? 0);
    if (byGroup !== 0) return byGroup;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

export function sortTransportApprovalSlotsForDisplay<
  T extends {
    id?: number;
    group_id?: number | null;
    status?: string | null;
    justification?: string | null;
  },
>(slots: T[]): T[] {
  return [...slots].sort((a, b) => {
    const byStatus =
      transportApprovalDisplaySortRank(a.status, a.justification) -
      transportApprovalDisplaySortRank(b.status, b.justification);
    if (byStatus !== 0) return byStatus;
    const byGroup = (a.group_id ?? 0) - (b.group_id ?? 0);
    if (byGroup !== 0) return byGroup;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

export function partitionTransportApprovalSlotsForDisplay<
  T extends { status?: string | null },
>(slots: T[]) {
  const sorted = sortTransportApprovalSlotsForDisplay(slots);
  const pending = sorted.filter((slot) => slot.status === 'pendente');
  const history = sorted.filter((slot) => slot.status !== 'pendente');
  return { pending, history };
}

/** Comitê: rodada vigente (último slot por área, não cancelado) vs histórico. */
export function partitionCommitteeApprovalSlotsForDisplay<
  T extends {
    id?: number;
    group_id?: number | null;
    track?: string | null;
    status?: string | null;
    assignee_id?: number | null;
  },
>(trackSlots: T[], allApprovals?: T[] | null) {
  const source = allApprovals ?? trackSlots;
  const activeIds = new Set(
    activeCommitteeApprovalSlots(source)
      .filter((item) => item.group_id)
      .map((item) => item.id),
  );
  const sorted = sortCommitteeApprovalSlotsForDisplay(trackSlots);
  const current = sorted.filter((slot) => slot.id != null && activeIds.has(slot.id));
  const history = sorted.filter((slot) => slot.id == null || !activeIds.has(slot.id));
  return { current, history };
}

export function latestTransportSlotForKey<
  T extends {
    id?: number;
    track?: string | null;
    group_id?: number | null;
    assignee_id?: number | null;
    status?: string | null;
  },
>(approvals: T[] | null | undefined, slot: T): T | undefined {
  const key = approvalSlotAreaKey(slot);
  const family = (approvals || []).filter(
    (item) => item.track === 'transporte' && approvalSlotAreaKey(item) === key,
  );
  if (!family.length) return undefined;
  return family.reduce((best, item) => ((item.id ?? 0) > (best.id ?? 0) ? item : best));
}

export function canRespawnSingleTransportSlot(
  approvals?: Array<{
    id?: number;
    track?: string | null;
    group_id?: number | null;
    assignee_id?: number | null;
    status?: string | null;
  }> | null,
  slot?: {
    id?: number;
    track?: string | null;
    group_id?: number | null;
    assignee_id?: number | null;
    status?: string | null;
  } | null,
) {
  if (!slot || slot.track !== 'transporte') return false;
  const latest = latestTransportSlotForKey(approvals, slot);
  if (!latest || latest.id !== slot.id) return false;
  if (!isRejectedTransportApprovalStatus(latest.status)) return false;
  const key = approvalSlotAreaKey(slot);
  return !(approvals || []).some(
    (item) =>
      item.track === 'transporte' &&
      approvalSlotAreaKey(item) === key &&
      item.status === 'pendente',
  );
}

export function hasTransportAreaEligibleForRespawn(
  approvals?: Array<{
    id?: number;
    track?: string | null;
    group_id?: number | null;
    assignee_id?: number | null;
    status?: string | null;
  }> | null,
) {
  return (approvals || []).some((slot) => canRespawnSingleTransportSlot(approvals, slot));
}

export function latestApprovalSlotsByKey<
  T extends {
    id?: number;
    track?: string | null;
    group_id?: number | null;
    assignee_id?: number | null;
  },
>(slots: T[], options?: { track?: string | null }): T[] {
  const latest = new Map<string, T>();
  for (const slot of slots) {
    if (options?.track != null && slot.track !== options.track) continue;
    const key = approvalSlotAreaKey(slot);
    const prev = latest.get(key);
    const prevId = prev?.id ?? 0;
    const slotId = slot.id ?? 0;
    if (!prev || slotId >= prevId) latest.set(key, slot);
  }
  return Array.from(latest.values());
}

export function countCommitteeApprovalSlots(
  approvals?: Array<{ group_id?: number | null; track?: string | null }> | null,
) {
  return (approvals || []).filter(
    (item) => item.group_id || item.track === 'criador_gestor' || item.track === 'gestao_mudancas',
  ).length;
}

export type ApprovalVoidScope = 'committee' | 'transport';

export function canVoidApprovals(
  change?: {
    status?: string | null;
    created_by?: number | null;
    requester_id?: number | null;
    change_owner_id?: number | null;
    approvals?: Array<{ group_id?: number | null; track?: string | null; status?: string | null }> | null;
  } | null,
  user?: Pick<User, 'id' | 'role' | 'can_manage_changes'> | null,
  settings?: Pick<ApprovalSettings, 'plan_editor_roles'> | null,
  scope: ApprovalVoidScope = 'committee',
) {
  if (!change) return false;
  if (scope === 'transport') {
    if (!canManageTransportApprovalOps(change, user)) return false;
    if (change.status !== 'transporte') return false;
    return (change.approvals || []).some(
      (item) =>
        item.track === 'transporte' &&
        item.group_id &&
        item.status !== 'cancelado',
    );
  }
  if (!canManageApprovalRespawn(change, user, settings)) return false;
  if (change.status !== 'autorizar') return false;
  const active = activeCommitteeApprovalSlots(change.approvals);
  if (!active.some((item) => item.group_id) && !active.some((item) => item.track === TRACK_GESTAO)) {
    return false;
  }
  return true;
}

/** Autorizar: plano editável enquanto houver área pendente, cancelada ou reprovada. */
export function canEditApprovalPlanByStatus(
  status?: string | null,
  approvals?: Array<{
    id?: number;
    group_id?: number | null;
    status?: string | null;
    track?: string | null;
    assignee_id?: number | null;
  }> | null,
) {
  const latest = latestApprovalSlotsByKey((approvals || []).filter(isCommitteeApprovalSlot));
  const needsRemount = latest.some((item) => item.status !== 'aprovado');
  if (status === 'agendado') {
    return latest.length > 0 && needsRemount;
  }
  if (status !== 'autorizar') return false;
  if (latest.length === 0) return true;
  return needsRemount;
}

/** Rodada atual acabou em reprovação/cancelamento — falta salvar o plano para nova pendência. */
export function committeeNeedsNewApprovalRound(
  approvals?: Array<{
    group_id?: number | null;
    status?: string | null;
    track?: string | null;
    assignee_id?: number | null;
    id?: number;
  }> | null,
) {
  const latest = latestApprovalSlotsByKey((approvals || []).filter(isCommitteeApprovalSlot));
  if (latest.length === 0) return false;
  if (latest.some((item) => item.status === 'pendente')) return false;
  return latest.some(
    (item) =>
      item.status === 'rejeitado' || item.status === 'reprovado' || item.status === 'cancelado',
  );
}

/** Antes de Autorizar o badge fica 0; depois, pendentes ou o total já decidido. */
export function approvalsBadgeCount(
  plan?: ApprovalPlan | null,
  pendingSlots = 0,
  status?: string | null,
  totalSlots = 0,
  approvals?: Array<{
    id?: number;
    group_id?: number | null;
    track?: string | null;
    status?: string | null;
    assignee_id?: number | null;
  }> | null,
) {
  const historyCount = countCommitteeApprovalSlots(approvals);
  if (status === 'agendado' && historyCount > 0) {
    const active = activeCommitteeApprovalSlots(approvals);
    return active.length > 0 ? active.length : historyCount;
  }
  if (status === 'avaliar' || status === 'agendado') {
    return countApprovalPlanAreas(plan);
  }
  const active = activeCommitteeApprovalSlots(approvals);
  const committeePending = active.filter((item) => item.status === 'pendente').length;
  if (committeePending > 0) return committeePending;
  if (active.length > 0) return active.length;
  return pendingSlots > 0 ? pendingSlots : totalSlots;
}
